import { projects } from "@folio-2026/content"
import Experience, {
  type Section as SectionNameType,
} from "@folio-2026/experience"
import { Menu } from "@folio-2026/menu"
import Resources, { AudioLoader } from "@folio-2026/resources"
import Section from "@folio-2026/section"
import ContactSection from "@folio-2026/section-contact"
import GuestbookSection from "@folio-2026/section-guestbook"
import LoaderSection from "@folio-2026/section-loader"
import WorksSection from "@folio-2026/section-works"
import SectionTransition from "@folio-2026/section/section-transition"
import { EVENTS } from "@folio-2026/shared"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"

import sources, { audioSources } from "components/sources"

const sectionClasses: Record<SectionNameType, typeof Section> = {
  loader: LoaderSection,
  works: WorksSection,
  guestbook: GuestbookSection,
  contact: ContactSection,
}

type SectionType = Record<SectionNameType, Section>

export default function useExperience() {
  const navigate = useNavigate()

  const [experience, setExperience] = useState<Experience | null>(null)

  const initialSectionA = useMemo(sectionNameFromPathname, [])

  const sections = useRef<SectionType | null>(null)
  const sectionTransition = useRef<SectionTransition | null>(null)

  const updateTransitionScenesCallback = useCallback(
    (params: { delta: number; elapsed: number }) => {
      if (!sections.current || !sectionTransition.current) {
        return
      }

      updateTransitionScenes({
        sections: sections.current,
        sectionTransition: sectionTransition.current,
        params,
      })
    },
    [],
  )

  const resizeScene = useCallback(() => {
    // Resize transition (camera + quad + sceneA/sceneB FBOs) first so viewport matches
    Object.values(sections.current || {}).forEach((section) => {
      if (section instanceof Section) {
        section.resize()
      }
    })
    sectionTransition.current?.resize()
  }, [])

  const updateAllSectionsOnce = useCallback(async () => {
    if (!sections.current || !experience) {
      console.warn("No sections to update", sections.current)
      return
    }

    const sectionsToUpdate = Object.values(sections.current)

    for (const section of sectionsToUpdate) {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          section.update({ delta: 0, elapsed: 0 })
          resolve()
        })
      })
    }

    experience.trigger(EVENTS.Loader.AllSectionsUpdated)
  }, [experience])

  useEffect(() => {
    const _experience = Experience.getInstance()
    _experience.onUpdate = updateTransitionScenesCallback
    _experience.onResize = resizeScene
    _experience.navigate = navigate

    let startPercentage: number | null = null
    _experience.on(EVENTS.Loader.Progress, (...args: unknown[]) => {
      const payload = args[0] as { percentage: number }
      if (startPercentage === null) startPercentage = payload.percentage
      const range = 100 - startPercentage
      const normalized =
        range > 0 ? ((payload.percentage - startPercentage) / range) * 100 : 100
      const el = document.getElementById("tcmg-loader-percentage")
      if (el) el.textContent = `${Math.round(normalized)}%`
    })

    // Create resources first (but don't wait for ready yet)
    const globalResources = new Resources(sources)
    const audioLoader = new AudioLoader(audioSources)

    const progressState = {
      globalLoaded: 0,
      globalTotal: 0,
      audioLoaded: 0,
      audioTotal: 0,
      sectionsProgress: [
        { loaded: 0, total: 0 },
        { loaded: 0, total: 0 },
        { loaded: 0, total: 0 },
        { loaded: 0, total: 0 },
      ] as { loaded: number; total: number }[],
    }

    function emitProgress() {
      const globalPct = progressState.globalTotal
        ? (progressState.globalLoaded / progressState.globalTotal) * 100
        : 0
      const audioPct = progressState.audioTotal
        ? (progressState.audioLoaded / progressState.audioTotal) * 100
        : 0
      const sectionPcts = progressState.sectionsProgress.map((s) =>
        s.total ? (s.loaded / s.total) * 100 : 0,
      )
      const sectionsPct =
        sectionPcts.length > 0
          ? sectionPcts.reduce((a, b) => a + b, 0) / sectionPcts.length
          : 0
      const combined = Math.min(
        100,
        Math.max(0, (globalPct + audioPct + sectionsPct) / 3),
      )
      _experience.trigger(EVENTS.Loader.Progress, [{ percentage: combined }])
    }

    globalResources.on("progress", (...args: unknown[]) => {
      const [, loaded, total] = args as [string, number, number]
      progressState.globalLoaded = loaded
      progressState.globalTotal = total
      emitProgress()
    })
    audioLoader.on("progress", (...args: unknown[]) => {
      const [, loaded, total] = args as [string, number, number]
      progressState.audioLoaded = loaded
      progressState.audioTotal = total
      emitProgress()
    })

    async function loadGlobalResources() {
      return new Promise<Resources>((resolve) => {
        globalResources.on("ready", () => {
          resolve(globalResources)
        })
      })
    }

    async function loadAudioResources() {
      return new Promise<void>((resolve) => {
        audioLoader.on("ready", () => {
          resolve()
        })
      })
    }

    Promise.all([loadGlobalResources(), loadAudioResources()]).then(
      async ([globalResources]) => {
        _experience.globalResources = globalResources.items
        _experience.audioLoader = audioLoader

        const _sections = await loadSectionsResources(
          (sectionIndex: number, loaded: number, total: number) => {
            progressState.sectionsProgress[sectionIndex] = { loaded, total }
            emitProgress()
          },
        )
        sections.current = _sections

        sectionTransition.current = initSectionTransition(
          sections.current,
          initialSectionA,
        )
        _experience.trigger(EVENTS.Loader.CurtainRemovable)
      },
    )

    // Initialize experience and renderer
    // Use async IIFE to properly await init() before setting up KTX2 renderer
    ;(async () => {
      await _experience.init()
      setExperience(_experience)
      window.experience = _experience

      // Initialize KTX2Loader with renderer (required for KTX2 textures)
      // This must be called after renderer is created to start loading GLB files
      // For WebGPU, await renderer.init() before calling detectSupport
      // After init(), renderer.instance should always be available
      if (globalResources && _experience.renderer?.instance) {
        globalResources
          .setKTX2Renderer(_experience.renderer.instance)
          .catch((error) => {
            console.error("Failed to initialize KTX2Loader:", error)
          })
      }
    })()
  }, [navigate, updateTransitionScenesCallback, initialSectionA, resizeScene])

  //
  // When the app is ready, call the update method of each sections and see if the transition performance improves
  useEffect(() => {
    if (experience) {
      experience.on(EVENTS.Loader.CurtainRemovable, updateAllSectionsOnce)
    }
  }, [experience, updateAllSectionsOnce])

  return experience
}

async function loadSectionsResources(
  onSectionProgress?: (
    sectionIndex: number,
    loaded: number,
    total: number,
  ) => void,
) {
  const _sectionClasses = Object.entries(sectionClasses)

  //
  // Load sections's resources
  const sections: SectionType = {} as SectionType

  await Promise.all(
    _sectionClasses.map(([key, value], index) => {
      sections[key as SectionNameType] = new value()
      const section = sections[key as SectionNameType]

      if (onSectionProgress) {
        section.resources.on("progress", (...args: unknown[]) => {
          const [, loaded, total] = args as [string, number, number]
          onSectionProgress(index, loaded, total)
        })
      }

      return new Promise((resolve) => {
        section.on("ready", () => {
          section.init()
          resolve(section)
        })
      })
    }),
  )

  return sections
}

function initSectionTransition(
  sections: SectionType,
  currentSectionName: SectionNameType = "loader",
) {
  if (
    !sections["loader"] ||
    !sections["works"] ||
    !sections["guestbook"] ||
    !sections["contact"]
  ) {
    throw new Error("Section is not defined")
  }

  const sceneA = sections[currentSectionName]

  if (!sceneA) {
    throw new Error("Scene A is not defined")
  }

  const sectionIndex = Object.keys(sections).indexOf(currentSectionName)
  const sectionNames = Object.keys(sectionClasses) as SectionNameType[]

  // Get the next or previous section
  const nextSectionName =
    sectionNames[sectionIndex + 1] || sectionNames[sectionIndex - 1]
  const sceneB = nextSectionName ? sections[nextSectionName] : undefined

  if (!sceneB) {
    throw new Error("Scene B is not defined")
  }

  const experience = Experience.getInstance()
  const menuDebugFolder =
    experience.debug?.active && experience.debug?.ui
      ? experience.debug.ui.addFolder({
          title: "📋 Menu",
          expanded: false,
        })
      : undefined
  const menu = new Menu({ debugFolder: menuDebugFolder })

  return new SectionTransition(sceneA, sceneB, menu)
}

function getNextSectionName(
  currentSectionName: SectionNameType,
): SectionNameType | null {
  const sectionNames = Object.keys(sectionClasses) as SectionNameType[]
  const currentIndex = sectionNames.indexOf(currentSectionName)
  if (currentIndex === -1 || currentIndex === sectionNames.length - 1) {
    return null
  }
  return sectionNames[currentIndex + 1]
}

function getPreviousSectionName(
  currentSectionName: SectionNameType,
): SectionNameType | null {
  const sectionNames = Object.keys(sectionClasses) as SectionNameType[]
  const currentIndex = sectionNames.indexOf(currentSectionName)
  if (currentIndex === -1 || currentIndex === 0) {
    return null
  }
  return sectionNames[currentIndex - 1]
}

function updateTransitionScenes({
  sections,
  sectionTransition,
  params,
}: {
  sections: SectionType
  sectionTransition: SectionTransition
  params: { delta: number; elapsed: number }
}) {
  const experience = Experience.getInstance()
  const scrollInfo = experience.scrollInfo

  // Transition
  if (
    sectionTransition &&
    scrollInfo &&
    Array.isArray(scrollInfo.snaps) &&
    typeof scrollInfo.actualScroll === "number" &&
    typeof scrollInfo.startingScroll === "number"
  ) {
    //
    //
    // Update sceneA and sceneB together to keep them synchronized
    if (
      scrollInfo.direction === 1 &&
      scrollInfo.currentSection &&
      sectionTransition.sceneA.name !== scrollInfo.currentSection.name
    ) {
      // console.log(
      //   "changeSceneA",
      //   sectionTransition.sceneA.name,
      //   scrollInfo.currentSection.name,
      // )
      sectionTransition.changeSceneA(
        sections[scrollInfo.currentSection.name] as Section,
      )

      // When changing sceneA during scroll down, also update sceneB to be the next section
      // This prevents the previous section from appearing
      if (scrollInfo.nextSection) {
        // console.log(
        //   "changeSceneB",
        //   sectionTransition.sceneB.name,
        //   scrollInfo.nextSection.name,
        // )
        sectionTransition.changeSceneB(
          sections[scrollInfo.nextSection.name] as Section,
        )
      } else {
        // Fallback: get next section from current section name
        const nextSectionName = getNextSectionName(
          scrollInfo.currentSection.name,
        )
        if (nextSectionName) {
          // console.log(
          //   "changeSceneB",
          //   sectionTransition.sceneB.name,
          //   nextSectionName,
          // )
          sectionTransition.changeSceneB(sections[nextSectionName] as Section)
        }
      }
    }

    // On scroll up flip the scenes
    if (
      scrollInfo.direction === -1 &&
      scrollInfo.nextSection &&
      sectionTransition.sceneA.name !== scrollInfo.nextSection.name
    ) {
      // console.log(
      //   "changeSceneA 2",
      //   sectionTransition.sceneA.name,
      //   scrollInfo.nextSection.name,
      // )
      sectionTransition.changeSceneA(
        sections[scrollInfo.nextSection.name] as Section,
      )

      // When changing sceneA during scroll up, also update sceneB to be the current section
      if (scrollInfo.currentSection) {
        // console.log(
        //   "changeSceneB",
        //   sectionTransition.sceneB.name,
        //   scrollInfo.currentSection.name,
        // )
        sectionTransition.changeSceneB(
          sections[scrollInfo.currentSection.name] as Section,
        )
      } else {
        // Fallback: get previous section from next section name
        const previousSectionName = getPreviousSectionName(
          scrollInfo.nextSection.name,
        )
        if (previousSectionName) {
          // console.log(
          //   "changeSceneB",
          //   sectionTransition.sceneB.name,
          //   previousSectionName,
          // )
          sectionTransition.changeSceneB(
            sections[previousSectionName] as Section,
          )
        }
      }
    }

    sectionTransition.render(scrollInfo.progress || 0, params)
  }
}

function sectionNameFromPathname() {
  const pathname = window.location.pathname

  //
  // Homepage
  //
  if (pathname === "/" || pathname === "/about") {
    return "loader"
  }

  //
  // Project page
  //
  if (projects.find((project) => project.url === pathname)) {
    return "works"
  }

  // Default to loader
  return "loader"
}
